// CreditNova Extraction Results Component
// Unified financial statement view with inline adjustments and lock mechanism.
// Each row shows: Line Item | Extracted Value | Adjusted Value | Variance | Lock
// Rows start locked. Clicking the adjusted value cell unlocks + enters edit.
// User must lock (or Lock All) to confirm. Continue is disabled while any row is unlocked.

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Download,
  CheckCircle,
  Info,
  Upload,
  Calculator,
  FileText,
  FileSpreadsheet,
  ChevronDown,
  ChevronUp,
  Lock,
  Unlock,
  AlertTriangle,
  RotateCcw,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { FinancialData, Note, ContentBlock, NoteTable } from '@creditnova/shared/schemas';

// Re-export Override type from here so financial.tsx can import it
export interface Override {
  fieldPath: string;
  fieldLabel: string;
  originalValue: string;
  overrideValue: string;
  section: 'incomeStatement' | 'balanceSheet' | 'cashFlow';
}

// ===========================================
// Types
// ===========================================

interface ExtractionResultsProps {
  data: FinancialData;
  /** Original (pre-override) financial data. When provided by the parent,
   *  this is used for the "Extracted Value" column instead of snapshotting
   *  from `data` on first render. This survives component remounts (e.g.
   *  tab switches) because the parent owns the stable reference. */
  originalData?: FinancialData;
  onDownloadJSON?: () => void;
  onDownloadCSV?: () => void;
  onSelectMetrics?: () => void;
  onUploadNew?: () => void;
  onPullEquifax?: () => void;
  onAddOffBalanceSheet?: () => void;
  showEquifaxButton?: boolean;
  nextStageButtonText?: string;
  /** Called whenever overrides change (locked adjusted values) */
  onOverridesChange?: (overrides: Override[]) => void;
  /** Called whenever the count of unlocked rows changes — parent uses this to disable Next */
  onUnlockedCountChange?: (count: number) => void;
  /** When true, all values are view-only — no unlock, edit, or lock actions. */
  readonly?: boolean;
}

// ===========================================
// Helpers (financial parsing)
// ===========================================

function parseFinancialValue(value: string): number | null {
  if (!value || value === '—' || value === '-' || value === 'N/A') return null;
  const isNegative = value.includes('(') && value.includes(')');
  const cleaned = value.replace(/[$,()%\s]/g, '');
  const num = parseFloat(cleaned);
  if (isNaN(num)) return null;
  return isNegative ? -num : num;
}

function formatFinancialValue(num: number): string {
  const absNum = Math.abs(num);
  const formatted =
    '$' +
    absNum.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  return num < 0 ? `(${formatted})` : formatted;
}

function calculateVariance(
  originalStr: string,
  adjustedStr: string
): { pct: string; direction: 'up' | 'down'; isNew?: boolean } | null {
  const original = parseFinancialValue(originalStr);
  const adjusted = parseFinancialValue(adjustedStr);
  if (original === null || adjusted === null) return null;
  if (original === adjusted) return null;
  // When original is zero but adjusted is non-zero, show as "NEW" indicator
  if (original === 0) {
    if (adjusted === 0) return null;
    return { pct: 'NEW', direction: adjusted > 0 ? 'up' : 'down', isNew: true };
  }
  const pct = ((adjusted - original) / Math.abs(original)) * 100;
  return {
    pct: Math.abs(pct).toFixed(1),
    direction: pct >= 0 ? 'up' : 'down',
  };
}

function isValidFinancialInput(value: string): boolean {
  const cleaned = value.replace(/[$,\s()]/g, '');
  if (cleaned === '' || cleaned === '-') return true;
  return !isNaN(parseFloat(cleaned));
}

const VARIANCE_THRESHOLD = 20;

// ===========================================
// AdjustableDataRow Component
// ===========================================

interface RowState {
  adjustedValue: string;
  isLocked: boolean;
}

interface AdjustableDataRowProps {
  label: string;
  extractedValue: string;
  rowState: RowState;
  note?: Note | undefined;
  onLockToggle: () => void;
  onValueChange: (newValue: string) => void;
  onReset: () => void;
  testId?: string;
  /** When true, the row is view-only — no unlock, edit, or lock actions. */
  readonly?: boolean;
}

function AdjustableDataRow({
  label,
  extractedValue,
  rowState,
  note,
  onLockToggle,
  onValueChange,
  onReset,
  testId,
  readonly,
}: AdjustableDataRowProps) {
  const { adjustedValue, isLocked } = rowState;
  const [editValue, setEditValue] = useState(adjustedValue);
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const editValueRef = useRef(editValue);

  // Sync editValue when adjustedValue changes externally (e.g. reset)
  useEffect(() => {
    if (isLocked) {
      setEditValue(adjustedValue);
      editValueRef.current = adjustedValue;
    }
  }, [adjustedValue, isLocked]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const isOverridden = adjustedValue !== extractedValue;
  // Use editValue for live variance feedback during editing, adjustedValue when locked/committed
  const varianceSource = isEditing ? editValue : adjustedValue;
  const variance = calculateVariance(extractedValue, varianceSource);
  const isHighVariance = variance
    ? variance.isNew || parseFloat(variance.pct) > VARIANCE_THRESHOLD
    : false;

  // Get a clean tooltip summary from note content
  const getTooltipContent = (note: Note): string => {
    if (note.content && note.content.length > 0) {
      const textBlocks = (note.content as ContentBlock[])
        .filter((block: ContentBlock) => block.type === 'text')
        .map((block: ContentBlock) => block.value)
        .join(' ');
      if (textBlocks.length > 300) {
        return textBlocks.substring(0, 300).trim() + '...';
      }
      return textBlocks || note.description || '';
    }
    const desc = note.description || '';
    if (desc.length > 300) {
      return desc.substring(0, 300).trim() + '...';
    }
    return desc;
  };

  const hasTable = note?.content?.some((block: ContentBlock) => block.type === 'table');

  const handleCellClick = () => {
    if (readonly) return; // View-only mode — no editing
    if (isLocked) {
      // Unlock and enter edit mode
      onLockToggle();
      setEditValue(adjustedValue);
      editValueRef.current = adjustedValue;
      setIsEditing(true);
    } else if (!isEditing) {
      setIsEditing(true);
    }
  };

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setEditValue(v);
    editValueRef.current = v;
  }, []);

  /** Commit the edit value to the row state (but row stays unlocked until user locks) */
  const commitValue = useCallback(() => {
    setIsEditing(false);
    const trimmed = editValueRef.current.trim();
    if (!trimmed || !isValidFinancialInput(trimmed)) {
      setEditValue(adjustedValue);
      editValueRef.current = adjustedValue;
      return;
    }
    const parsed = parseFinancialValue(trimmed);
    if (parsed === null) {
      setEditValue(adjustedValue);
      editValueRef.current = adjustedValue;
      return;
    }
    const formatted = formatFinancialValue(parsed);
    setEditValue(formatted);
    editValueRef.current = formatted;
    onValueChange(formatted);
  }, [adjustedValue, onValueChange]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        commitValue();
        // Also lock on Enter for convenience
        // Use setTimeout to let commitValue finish first
        setTimeout(() => onLockToggle(), 0);
      } else if (e.key === 'Escape') {
        setIsEditing(false);
        setEditValue(adjustedValue);
        editValueRef.current = adjustedValue;
      }
    },
    [commitValue, adjustedValue, onLockToggle]
  );

  const handleLockClick = () => {
    if (readonly) return; // View-only mode
    if (!isLocked && isEditing) {
      // Commit current value first, then lock
      commitValue();
    }
    onLockToggle();
  };

  const handleReset = () => {
    onReset();
    setEditValue(extractedValue);
    editValueRef.current = extractedValue;
    setIsEditing(false);
  };

  return (
    <div
      className={cn(
        'grid grid-cols-[1fr_150px_170px_90px_70px] items-center py-3 px-4 border-b border-border/40 transition-colors',
        !isLocked && 'bg-amber-500/5',
        isOverridden && isLocked && 'bg-blue-500/5'
      )}
      data-testid={testId}
    >
      {/* Line Item */}
      <div className="flex items-center gap-2 min-w-0">
        <span
          className={cn(
            'text-sm truncate',
            isOverridden ? 'text-blue-400 font-medium' : 'text-foreground'
          )}
        >
          {label}
        </span>
        {note && (
          <TooltipProvider>
            <Tooltip delayDuration={200}>
              <TooltipTrigger asChild>
                <button className="flex items-center justify-center w-5 h-5 rounded-full bg-primary/10 hover:bg-primary/20 transition-colors shrink-0">
                  <Info className="w-3 h-3 text-primary" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-sm">
                <div className="space-y-1">
                  {note.noteReference && (
                    <p className="text-xs font-semibold text-primary">{note.noteReference}</p>
                  )}
                  {note.noteTitle && <p className="text-xs font-medium">{note.noteTitle}</p>}
                  <p className="text-sm text-muted-foreground">{getTooltipContent(note)}</p>
                  {hasTable && (
                    <p className="text-xs text-muted-foreground italic">
                      View note for full details including tables
                    </p>
                  )}
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {/* Extracted Value */}
      <div className="text-right font-mono text-sm text-muted-foreground">{extractedValue}</div>

      {/* Adjusted Value — click to unlock + edit */}
      <div className="text-right">
        {isEditing && !isLocked ? (
          <input
            ref={inputRef}
            type="text"
            value={editValue}
            onChange={handleChange}
            onBlur={commitValue}
            onKeyDown={handleKeyDown}
            className={cn(
              'w-full text-right font-mono text-sm bg-amber-500/20 border border-amber-500/50 rounded px-2 py-1',
              'text-foreground outline-none focus:ring-1 focus:ring-amber-500/50'
            )}
          />
        ) : (
          <span
            onClick={handleCellClick}
            className={cn(
              'inline-block font-mono text-sm rounded px-2 py-1 transition-colors',
              isLocked
                ? isOverridden
                  ? 'text-blue-400 font-semibold cursor-pointer hover:bg-blue-500/10'
                  : 'text-foreground cursor-pointer hover:bg-muted/50'
                : 'text-amber-400 font-semibold bg-amber-500/15 border border-amber-500/30 cursor-pointer'
            )}
          >
            {adjustedValue}
          </span>
        )}
      </div>

      {/* Variance */}
      <div className="text-right text-sm">
        {variance ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className={cn(
                    'inline-flex items-center gap-1',
                    isHighVariance ? 'text-amber-400' : 'text-muted-foreground'
                  )}
                >
                  {isHighVariance && <AlertTriangle className="w-3 h-3" />}
                  <span className="font-mono">
                    {variance.direction === 'up' ? '\u25B2' : '\u25BC'}
                    {variance.isNew ? 'NEW' : `${variance.pct}%`}
                  </span>
                </span>
              </TooltipTrigger>
              {isHighVariance && (
                <TooltipContent side="left">
                  <p className="text-xs">
                    {variance.isNew
                      ? 'Value changed from $0. Please confirm this adjustment is intentional.'
                      : `Variance exceeds ${VARIANCE_THRESHOLD}% threshold. Please confirm this adjustment is intentional.`}
                  </p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </div>

      {/* Lock + Reset */}
      <div className="flex items-center justify-end gap-1">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={handleLockClick}
                className={cn(
                  'p-1.5 rounded transition-colors',
                  isLocked
                    ? 'text-green-500 hover:bg-green-500/10'
                    : 'text-amber-400 hover:bg-amber-500/10 animate-pulse'
                )}
                title={isLocked ? 'Click to unlock and edit' : 'Click to lock value'}
              >
                {isLocked ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
              </button>
            </TooltipTrigger>
            <TooltipContent side="left">
              <p className="text-xs">
                {isLocked ? 'Locked — click to edit' : 'Unlocked — click to confirm value'}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        {isOverridden && (
          <button
            onClick={handleReset}
            className="p-1 rounded hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-colors"
            title="Reset to extracted value"
          >
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

// ===========================================
// AdjustableStatementSection Component
// ===========================================

interface AdjustableStatementSectionProps {
  items: Record<string, string>;
  originalItems: Record<string, string>;
  notes?: Note[];
  section: 'incomeStatement' | 'balanceSheet' | 'cashFlow';
  sectionName: string;
  rowStates: Map<string, RowState>;
  onLockToggle: (key: string) => void;
  onValueChange: (key: string, newValue: string) => void;
  onReset: (key: string) => void;
  readonly?: boolean;
}

function AdjustableStatementSection({
  items,
  originalItems,
  notes,
  section,
  sectionName,
  rowStates,
  onLockToggle,
  onValueChange,
  onReset,
  readonly,
}: AdjustableStatementSectionProps) {
  // Create a map of line items to their notes for quick lookup
  const noteMap = new Map<string, Note>();
  if (notes) {
    notes.forEach((note) => {
      if (note.lineItem) {
        noteMap.set(note.lineItem, note);
      }
    });
  }

  // Capture the initial key order once so row positions never shift when
  // the parent replaces the `items` object (e.g. after an override is applied).
  // New keys that appear later are appended at the end.
  const stableKeysRef = useRef<string[] | null>(null);
  if (stableKeysRef.current === null && items && Object.keys(items).length > 0) {
    stableKeysRef.current = Object.keys(items);
  }

  // Merge: stable order first, then any new keys appended
  const orderedKeys = useMemo(() => {
    const currentKeys = items ? Object.keys(items) : [];
    if (!stableKeysRef.current) return currentKeys;
    const known = new Set(stableKeysRef.current);
    const extras = currentKeys.filter((k) => !known.has(k));
    // Only include keys that still exist in items
    const currentSet = new Set(currentKeys);
    return [...stableKeysRef.current.filter((k) => currentSet.has(k)), ...extras];
  }, [items]);

  if (!items || orderedKeys.length === 0) {
    return (
      <div className="py-8 text-center text-muted-foreground">
        No {sectionName.replace('-', ' ')} data available
      </div>
    );
  }

  return (
    <div>
      {/* Column headers */}
      <div className="grid grid-cols-[1fr_150px_170px_90px_70px] items-center py-2 px-4 border-b border-border text-xs font-medium text-muted-foreground uppercase tracking-wider">
        <div>Line Item</div>
        <div className="text-right">Extracted Value</div>
        <div className="text-right">Adjusted Value</div>
        <div className="text-right">Variance</div>
        <div />
      </div>

      {/* Data rows — rendered in stable order captured on first mount */}
      {orderedKeys.map((label, index) => {
        const value = items[label];
        const key = `${section}.${label}`;
        const note = noteMap.get(label);
        const state = rowStates.get(key) || { adjustedValue: value, isLocked: true };
        const originalValue = originalItems[label] ?? value;

        return (
          <AdjustableDataRow
            key={key}
            label={label}
            extractedValue={originalValue}
            rowState={state}
            note={note}
            onLockToggle={() => onLockToggle(key)}
            onValueChange={(newValue) => onValueChange(key, newValue)}
            onReset={() => onReset(key)}
            testId={`data-${sectionName}-${index}`}
            readonly={readonly}
          />
        );
      })}
    </div>
  );
}

// ===========================================
// NoteTableRenderer Component
// ===========================================

interface NoteTableRendererProps {
  table: NoteTable;
  className?: string;
}

function NoteTableRenderer({ table, className = '' }: NoteTableRendererProps) {
  const grid = useMemo(() => {
    const result: string[][] = Array(table.rowCount)
      .fill(null)
      .map(() => Array(table.columnCount).fill(''));
    for (const cell of table.cells) {
      if (cell.rowIndex < table.rowCount && cell.columnIndex < table.columnCount) {
        result[cell.rowIndex][cell.columnIndex] = cell.content.trim();
      }
    }
    return result;
  }, [table]);

  if (grid.length === 0) return null;

  return (
    <div className={`overflow-x-auto my-3 ${className}`}>
      <table className="min-w-full text-sm border-collapse">
        <thead>
          <tr className="border-b-2 border-border">
            {grid[0].map((cell: string, colIdx: number) => (
              <th
                key={colIdx}
                className={`py-2 px-3 font-semibold text-foreground ${
                  colIdx === 0 ? 'text-left' : 'text-right'
                }`}
              >
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {grid.slice(1).map((row: string[], rowIdx: number) => (
            <tr key={rowIdx} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
              {row.map((cell: string, colIdx: number) => (
                <td
                  key={colIdx}
                  className={`py-2 px-3 ${
                    colIdx === 0 ? 'text-left text-muted-foreground' : 'text-right font-mono'
                  }`}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ===========================================
// NoteContentRenderer Component
// ===========================================

interface NoteContentRendererProps {
  content: ContentBlock[];
  isExpanded: boolean;
  charLimit: number;
}

function NoteContentRenderer({ content, isExpanded, charLimit }: NoteContentRendererProps) {
  const totalTextLength = useMemo(() => {
    return content
      .filter((block) => block.type === 'text')
      .reduce((sum, block) => sum + (block.value?.length || 0), 0);
  }, [content]);

  const shouldTruncate = !isExpanded && totalTextLength > charLimit;
  let remainingChars = charLimit;

  return (
    <div className="space-y-3">
      {content.map((block, idx) => {
        if (block.type === 'text') {
          const text = block.value || '';
          if (shouldTruncate) {
            if (remainingChars <= 0) return null;
            if (text.length > remainingChars) {
              const truncatedText = text.substring(0, remainingChars) + '...';
              remainingChars = 0;
              return (
                <p
                  key={idx}
                  className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap"
                >
                  {truncatedText}
                </p>
              );
            }
            remainingChars -= text.length;
          }
          return (
            <p
              key={idx}
              className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap"
            >
              {text}
            </p>
          );
        }
        if (block.type === 'table') {
          if (shouldTruncate && remainingChars <= 0) return null;
          return <NoteTableRenderer key={idx} table={block.value} />;
        }
        return null;
      })}
    </div>
  );
}

// ===========================================
// ExpandableNoteCard Component
// ===========================================

interface ExpandableNoteCardProps {
  note: Note;
  index: number;
}

function ExpandableNoteCard({ note, index }: ExpandableNoteCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const CHAR_LIMIT = 400;

  const hasContentBlocks = note.content && note.content.length > 0;
  const description = note.description || '';

  const isTruncated = useMemo(() => {
    if (hasContentBlocks) {
      const totalTextLength = (note.content! as ContentBlock[])
        .filter((block: ContentBlock) => block.type === 'text')
        .reduce((sum: number, block: ContentBlock) => sum + (typeof block.value === 'string' ? block.value.length : 0), 0);
      return totalTextLength > CHAR_LIMIT;
    }
    return description.length > CHAR_LIMIT;
  }, [hasContentBlocks, note.content, description]);

  return (
    <Card
      className="border-l-4 border-l-primary hover:shadow-md transition-shadow cursor-pointer"
      data-testid={`note-card-${index}`}
      onClick={() => isTruncated && setIsExpanded(!isExpanded)}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              {note.noteReference && (
                <span className="text-xs font-semibold text-primary">{note.noteReference}</span>
              )}
              <CardTitle className="text-base font-semibold">
                {note.noteTitle || note.lineItem}
              </CardTitle>
            </div>
            <CardDescription className="text-xs">Line Item: {note.lineItem}</CardDescription>
          </div>
          {isTruncated && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
              className="shrink-0"
              data-testid={`button-expand-note-${index}`}
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="w-4 h-4 mr-1" />
                  Show Less
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4 mr-1" />
                  Read More
                </>
              )}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {hasContentBlocks ? (
          <NoteContentRenderer content={note.content!} isExpanded={isExpanded} charLimit={CHAR_LIMIT} />
        ) : (
          <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">
            {isExpanded || !isTruncated
              ? description
              : description.substring(0, CHAR_LIMIT) + '...'}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// ===========================================
// ExtractionResults Main Component
// ===========================================

export function ExtractionResults({
  data,
  originalData,
  onDownloadJSON,
  onDownloadCSV,
  onSelectMetrics,
  onUploadNew,
  onPullEquifax,
  onAddOffBalanceSheet,
  showEquifaxButton = false,
  nextStageButtonText = 'Continue',
  onOverridesChange,
  onUnlockedCountChange,
  readonly,
}: ExtractionResultsProps) {
  const financialData = data.data;
  const notes = financialData.notes || [];

  // -----------------------------------------------
  // Preserve original extraction values so the "Extracted Value"
  // column never changes when overrides mutate the data prop.
  // When the parent provides `originalData` (e.g. from a stable ref
  // that survives tab switches), prefer that over snapshotting from
  // `data` which may already contain overrides on remount.
  // -----------------------------------------------
  const origSource = originalData?.data ?? financialData;
  const originalDataRef = useRef<{
    incomeStatement: Record<string, string>;
    balanceSheet: Record<string, string>;
    cashFlow: Record<string, string>;
  }>({
    incomeStatement: { ...(origSource.incomeStatement || {}) },
    balanceSheet: { ...(origSource.balanceSheet || {}) },
    cashFlow: { ...(origSource.cashFlow || {}) },
  });

  // -----------------------------------------------
  // Row state management: Map<fieldPath, RowState>
  // All rows start locked with adjustedValue = extractedValue
  // -----------------------------------------------
  const [rowStates, setRowStates] = useState<Map<string, RowState>>(() => {
    const initial = new Map<string, RowState>();
    const sections = ['incomeStatement', 'balanceSheet', 'cashFlow'] as const;
    for (const section of sections) {
      const items = financialData[section] as Record<string, string> | null;
      if (items) {
        for (const [label, value] of Object.entries(items)) {
          initial.set(`${section}.${label}`, { adjustedValue: value as string, isLocked: true });
        }
      }
    }
    return initial;
  });

  // Track unlocked count and notify parent
  const unlockedCount = useMemo(() => {
    let count = 0;
    rowStates.forEach((s: RowState) => {
      if (!s.isLocked) count++;
    });
    return count;
  }, [rowStates]);

  // Notify parent of unlocked count changes
  const prevUnlockedRef = useRef(unlockedCount);
  useEffect(() => {
    if (prevUnlockedRef.current !== unlockedCount) {
      prevUnlockedRef.current = unlockedCount;
      onUnlockedCountChange?.(unlockedCount);
    }
  }, [unlockedCount, onUnlockedCountChange]);

  // Also call on mount to set initial state
  useEffect(() => {
    onUnlockedCountChange?.(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track override count (compare against original, not mutated data)
  const overrideCount = useMemo(() => {
    let count = 0;
    const sections = ['incomeStatement', 'balanceSheet', 'cashFlow'] as const;
    for (const section of sections) {
      const items = originalDataRef.current[section];
      if (items) {
        for (const [label, value] of Object.entries(items)) {
          const key = `${section}.${label}`;
          const state = rowStates.get(key);
          if (state && state.adjustedValue !== (value as string)) count++;
        }
      }
    }
    return count;
  }, [rowStates]);

  // Section override counts for tab badges
  const sectionOverrideCounts = useMemo(() => {
    const counts = { incomeStatement: 0, balanceSheet: 0, cashFlow: 0 };
    const sections = ['incomeStatement', 'balanceSheet', 'cashFlow'] as const;
    for (const section of sections) {
      const items = originalDataRef.current[section];
      if (items) {
        for (const [label, value] of Object.entries(items)) {
          const key = `${section}.${label}`;
          const state = rowStates.get(key);
          if (state && state.adjustedValue !== (value as string)) counts[section]++;
        }
      }
    }
    return counts;
  }, [rowStates]);

  /** Build overrides array from current row states and notify parent */
  const emitOverrides = useCallback(
    (states: Map<string, RowState>) => {
      const overrides: Override[] = [];
      const sections = ['incomeStatement', 'balanceSheet', 'cashFlow'] as const;
      for (const section of sections) {
        const items = originalDataRef.current[section];
        if (items) {
          for (const [label, value] of Object.entries(items)) {
            const key = `${section}.${label}`;
            const state = states.get(key);
            if (state && state.adjustedValue !== (value as string) && state.isLocked) {
              overrides.push({
                fieldPath: key,
                fieldLabel: label,
                originalValue: value as string,
                overrideValue: state.adjustedValue,
                section,
              });
            }
          }
        }
      }
      onOverridesChange?.(overrides);
    },
    [onOverridesChange]
  );

  const handleLockToggle = useCallback(
    (key: string) => {
      setRowStates((prev: Map<string, RowState>) => {
        const next = new Map(prev);
        const current = next.get(key);
        if (current) {
          next.set(key, { ...current, isLocked: !current.isLocked });
        }
        // When locking, emit updated overrides
        if (current && !current.isLocked) {
          // Will be locked after this update
          setTimeout(() => emitOverrides(next), 0);
        }
        return next;
      });
    },
    [emitOverrides]
  );

  const handleValueChange = useCallback((key: string, newValue: string) => {
    setRowStates((prev) => {
      const next = new Map(prev);
      const current = next.get(key);
      if (current) {
        next.set(key, { ...current, adjustedValue: newValue });
      }
      return next;
    });
  }, []);

  const handleReset = useCallback(
    (key: string) => {
      const [section, ...rest] = key.split('.');
      const label = rest.join('.');
      const items = originalDataRef.current[section as keyof typeof originalDataRef.current];
      const originalValue = items?.[label] || '';

      setRowStates((prev: Map<string, RowState>) => {
        const next = new Map(prev);
        next.set(key, { adjustedValue: originalValue, isLocked: true });
        setTimeout(() => emitOverrides(next), 0);
        return next;
      });
    },
    [emitOverrides]
  );

  const handleLockAll = useCallback(() => {
    setRowStates((prev) => {
      const next = new Map(prev);
      next.forEach((state: RowState, key: string) => {
        if (!state.isLocked) {
          next.set(key, { ...state, isLocked: true });
        }
      });
      setTimeout(() => emitOverrides(next), 0);
      return next;
    });
  }, [emitOverrides]);

  // -----------------------------------------------
  // Display logic
  // -----------------------------------------------

  const detailLevelLabels: Record<string, string> = {
    'high-level': 'High Level',
    detailed: 'Detailed',
    'in-depth': 'In-Depth',
  };

  const hasIncomeStatement =
    financialData.incomeStatement && Object.keys(financialData.incomeStatement).length > 0;
  const hasBalanceSheet =
    financialData.balanceSheet && Object.keys(financialData.balanceSheet).length > 0;
  const hasCashFlow =
    financialData.cashFlow && Object.keys(financialData.cashFlow).length > 0;
  const hasNotes = notes && notes.length > 0;

  const defaultTab = hasIncomeStatement
    ? 'income'
    : hasBalanceSheet
    ? 'balance'
    : hasCashFlow
    ? 'cashflow'
    : 'income';

  // Download handlers
  const handleDownloadJSON = () => {
    if (onDownloadJSON) {
      onDownloadJSON();
    } else {
      const jsonData = JSON.stringify(data, null, 2);
      const blob = new Blob([jsonData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `extraction-${data.companyName || 'data'}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const handleDownloadCSV = () => {
    if (onDownloadCSV) {
      onDownloadCSV();
    } else {
      const rows: string[] = ['Section,Line Item,Extracted Value,Adjusted Value'];
      const sections = [
        { key: 'incomeStatement' as const, label: 'Income Statement' },
        { key: 'balanceSheet' as const, label: 'Balance Sheet' },
        { key: 'cashFlow' as const, label: 'Cash Flow' },
      ];
      for (const { key, label } of sections) {
        const items = financialData[key];
        const origItems = originalDataRef.current[key];
        if (items) {
          Object.entries(items).forEach(([lineItem, value]) => {
            const state = rowStates.get(`${key}.${lineItem}`);
            const adjusted = state ? state.adjustedValue : value;
            const originalValue = origItems?.[lineItem] ?? value;
            rows.push(`${label},"${lineItem}","${originalValue}","${adjusted}"`);
          });
        }
      }
      const csvData = rows.join('\n');
      const blob = new Blob([csvData], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `extraction-${data.companyName || 'data'}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  return (
    <Card data-testid="card-extraction-results">
      <CardHeader>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <CardTitle>Extraction Complete</CardTitle>
            </div>
            <CardDescription>
              {data.companyName && `${data.companyName} • `}
              {data.periodEnding && `Period Ending: ${data.periodEnding}`}
              {data.detailLevel && ` • ${detailLevelLabels[data.detailLevel]} Detail`}
              {notes.length > 0 && ` • ${notes.length} Note${notes.length > 1 ? 's' : ''}`}
            </CardDescription>
          </div>
          <div className="flex gap-2 items-center">
            {overrideCount > 0 && (
              <Badge variant="secondary" className="text-xs">
                {overrideCount} adjusted
              </Badge>
            )}
            <Button variant="outline" size="sm" onClick={handleDownloadJSON} data-testid="button-download-json">
              <Download className="w-4 h-4 mr-2" />
              JSON
            </Button>
            <Button variant="outline" size="sm" onClick={handleDownloadCSV} data-testid="button-download-csv">
              <Download className="w-4 h-4 mr-2" />
              CSV
            </Button>
          </div>
        </div>

        {/* Lock All bar — hidden in readonly mode */}
        {!readonly && (
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-border/40">
          <p className="text-xs text-muted-foreground">
            Click any adjusted value to modify it. Lock values to confirm. All values must be locked to continue.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={handleLockAll}
            disabled={unlockedCount === 0}
            className="shrink-0"
          >
            <Lock className="w-3.5 h-3.5 mr-1.5" />
            Lock All
            {unlockedCount > 0 && (
              <Badge variant="warning" className="ml-1.5 text-[10px] px-1 py-0">
                {unlockedCount}
              </Badge>
            )}
          </Button>
        </div>
        )}

        {/* Inline warning for unlocked values */}
        {unlockedCount > 0 && (
          <div className="flex items-center gap-2 mt-3 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30">
            <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
            <p className="text-xs text-amber-400">
              {unlockedCount} value{unlockedCount !== 1 ? 's are' : ' is'} unlocked. Lock all values
              to confirm your adjustments before continuing.
            </p>
          </div>
        )}
      </CardHeader>

      <CardContent>
        <Tabs defaultValue={defaultTab} className="w-full">
          <TabsList className={`grid w-full ${hasNotes ? 'grid-cols-4' : 'grid-cols-3'}`}>
            <TabsTrigger value="income" data-testid="tab-income-statement" disabled={!hasIncomeStatement}>
              Income Statement
              {sectionOverrideCounts.incomeStatement > 0 && (
                <Badge variant="secondary" className="ml-1.5 text-[10px] px-1 py-0">
                  {sectionOverrideCounts.incomeStatement}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="balance" data-testid="tab-balance-sheet" disabled={!hasBalanceSheet}>
              Balance Sheet
              {sectionOverrideCounts.balanceSheet > 0 && (
                <Badge variant="secondary" className="ml-1.5 text-[10px] px-1 py-0">
                  {sectionOverrideCounts.balanceSheet}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="cashflow" data-testid="tab-cash-flow" disabled={!hasCashFlow}>
              Cash Flow
              {sectionOverrideCounts.cashFlow > 0 && (
                <Badge variant="secondary" className="ml-1.5 text-[10px] px-1 py-0">
                  {sectionOverrideCounts.cashFlow}
                </Badge>
              )}
            </TabsTrigger>
            {hasNotes && (
              <TabsTrigger value="notes" data-testid="tab-notes">
                Notes ({notes.length})
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="income" className="mt-6">
            <AdjustableStatementSection
              items={financialData.incomeStatement || {}}
              originalItems={originalDataRef.current.incomeStatement}
              notes={notes}
              section="incomeStatement"
              sectionName="income-statement"
              rowStates={rowStates}
              onLockToggle={handleLockToggle}
              onValueChange={handleValueChange}
              onReset={handleReset}
              readonly={readonly}
            />
          </TabsContent>

          <TabsContent value="balance" className="mt-6">
            <AdjustableStatementSection
              items={financialData.balanceSheet || {}}
              originalItems={originalDataRef.current.balanceSheet}
              notes={notes}
              section="balanceSheet"
              sectionName="balance-sheet"
              rowStates={rowStates}
              onLockToggle={handleLockToggle}
              onValueChange={handleValueChange}
              onReset={handleReset}
              readonly={readonly}
            />
          </TabsContent>

          <TabsContent value="cashflow" className="mt-6">
            <AdjustableStatementSection
              items={financialData.cashFlow || {}}
              originalItems={originalDataRef.current.cashFlow}
              notes={notes}
              section="cashFlow"
              sectionName="cash-flow"
              rowStates={rowStates}
              onLockToggle={handleLockToggle}
              onValueChange={handleValueChange}
              onReset={handleReset}
              readonly={readonly}
            />
          </TabsContent>

          {hasNotes && (
            <TabsContent value="notes" className="mt-6">
              <div className="space-y-4">
                {notes
                  .slice()
                  .sort((a, b) => {
                    const numA = a.noteReference ? parseInt(a.noteReference.replace(/\D/g, '')) || 0 : 0;
                    const numB = b.noteReference ? parseInt(b.noteReference.replace(/\D/g, '')) || 0 : 0;
                    return numA - numB;
                  })
                  .map((note, index) => (
                    <ExpandableNoteCard key={index} note={note} index={index} />
                  ))}
              </div>
            </TabsContent>
          )}
        </Tabs>

        <div className="flex gap-3 mt-6 pt-6 border-t flex-wrap">
          {onUploadNew && (
            <Button variant="outline" onClick={onUploadNew} data-testid="button-upload-new" className="flex-1 min-w-[200px]">
              <Upload className="w-4 h-4 mr-2" />
              Upload New Document
            </Button>
          )}
          {onAddOffBalanceSheet && (
            <Button
              variant="outline"
              onClick={onAddOffBalanceSheet}
              data-testid="button-add-off-balance-sheet"
              className="flex-1 min-w-[200px]"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Add Off-Balance Sheet Records
            </Button>
          )}
          {onSelectMetrics && (
            <Button
              onClick={onSelectMetrics}
              data-testid="button-select-metrics"
              className="flex-1 min-w-[200px]"
            >
              <Calculator className="w-4 h-4 mr-2" />
              Select Analysis Metrics
            </Button>
          )}
          {showEquifaxButton && onPullEquifax && (
            <Button
              onClick={onPullEquifax}
              data-testid="button-continue-next-stage"
              className="flex-1 min-w-[200px]"
              variant="default"
            >
              <FileText className="w-4 h-4 mr-2" />
              {nextStageButtonText}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Export components for use in other components
export { ExpandableNoteCard, NoteTableRenderer };
